// Watch parties & listening rooms. State lives in encrypted conversation messages; this
// store tracks which party *this device* has joined and sends control events.
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import type { Party, PartyMedia, Uuid } from '../api/types'
import { newConversationId } from '../core/wasm'
import { currentParty, newPartyId, serially } from '../lib/party'
import { useSession } from './session'

const PRESENCE_HEARTBEAT_MS = 20_000

export const useParty = defineStore('party', () => {
  const joined = ref<{ conv: Uuid; id: string } | null>(null)

  const active = computed(() => {
    const session = useSession()
    const j = joined.value
    if (!j) return null
    const p = currentParty(session.messages[j.conv] ?? [])
    return p && p.party.id === j.id ? p : null
  })

  function serverNow(): number {
    return useSession().engine?.clock.serverNow() ?? Date.now()
  }

  async function send(conv: Uuid, party: Party): Promise<void> {
    const session = useSession()
    await session.engine!.sendEnvelope(conv, { v: 1, id: newConversationId(), ts: Date.now(), type: 'party', party })
  }

  /** Starts a party in `conv` with a queue of media, and joins it. The server's say on
   * whether this group may have one (friend-scale limits) comes first. */
  async function start(conv: Uuid, queue: PartyMedia[]): Promise<void> {
    await inOrder(() => useSession().api.partyPresence(conv, true))
    const id = newPartyId()
    joined.value = { conv, id }
    await send(conv, { id, action: 'start', queue, index: 0, position_ms: 0, playing: true, at_ms: serverNow() })
  }

  function join(conv: Uuid, id: string): void {
    joined.value = { conv, id }
  }

  /** Leaves; the last one out ends it, so it doesn't linger as joinable. */
  async function leave(): Promise<void> {
    const j = joined.value
    const session = useSession()
    const present = j ? (session.inParty[j.conv] ?? []) : []
    if (j && active.value && present.every((u) => u === session.me)) return end()
    joined.value = null
  }

  /** Broadcasts a change (play/pause/seek/skip, or a queue edit) made on this device. */
  async function update(change: Partial<Pick<Party, 'index' | 'position_ms' | 'playing' | 'queue'>>): Promise<void> {
    const a = active.value
    const j = joined.value
    if (!a || !j) return
    await send(j.conv, { ...a.party, ...change, action: 'state', at_ms: serverNow() })
  }

  async function end(): Promise<void> {
    const a = active.value
    const j = joined.value
    if (a && j) await send(j.conv, { ...a.party, action: 'end', playing: false, at_ms: serverNow() })
    joined.value = null
  }

  // Presence: while this device is in a party, tell the server (so the channel shows who's
  // watching, like a call). What's playing stays in the encrypted messages.
  let heartbeat: ReturnType<typeof setInterval> | null = null
  let presentIn: Uuid | null = null
  // Presence calls go out one after another, so a heartbeat still in flight can never
  // reach the server after "left" and mark us present again (HRTH-132).
  const inOrder = serially()
  function present(conv: Uuid | null): void {
    const session = useSession()
    if (heartbeat) clearInterval(heartbeat)
    heartbeat = null
    if (presentIn && presentIn !== conv) {
      const left = presentIn
      void inOrder(() => session.api.partyPresence(left, false))
        .then(({ participants }) => (session.inParty = { ...session.inParty, [left]: participants }))
        .catch(() => undefined)
    }
    presentIn = conv
    if (!conv) return
    const beat = () =>
      inOrder(() => (presentIn === conv ? session.api.partyPresence(conv, true) : Promise.reject(new Error('left'))))
        .then(({ participants }) => (session.inParty = { ...session.inParty, [conv]: participants }))
        .catch((e: { code?: string; message?: string }) => {
          // Full, or the group is bigger than this server allows parties for: say so and leave.
          if (e.code === 'party_full' || e.code === 'space_too_large') {
            session.toast(e.message ?? 'You can’t join this party', 'error')
            if (joined.value?.conv === conv) joined.value = null
          }
        })
    void beat()
    heartbeat = setInterval(beat, PRESENCE_HEARTBEAT_MS)
  }
  watch(
    () => (joined.value && active.value ? joined.value.conv : null),
    (conv) => present(conv),
  )
  // The party ended (by anyone): this device is no longer in it.
  watch(active, (a, before) => {
    if (!a && before) joined.value = null
  })

  return { joined, active, start, join, leave, update, end, serverNow }
})
